<template>
    <page-layout>
        <template>
            <div v-if="featuredProposal" class="proposal-container featured-proposal">
                <div class="columns-wrapper">
                    <div class="proposal-image">
                        <img :src="featuredProposal.activation.image" :alt="featuredProposal.activation.public_name">
                    </div>
                    <div class="proposal-info">
                        <h3>{{ featuredProposal.activation.public_name }}</h3>
                        <p class="submission-date">You submitted this proposal on {{ formatDate(featuredProposal.submitted_at || featuredProposal.created_at) }}</p>
                        <p class="notes-label">Your Notes:</p>
                    </div>
                    <div class="proposal-status">
                        <span class="status-badge" :class="getStatusBadgeClass(featuredProposal.status)">
                            {{ formatStatus(featuredProposal.status) }}
                        </span>
                    </div>
                </div>

                <div class="proposal-actions">
                    <p>You can now see the tasks required in order to complete this opportunity.</p>
                    <button class="view-details-btn" @click="navigateToOverview(featuredProposal.id)">View Details</button>
                </div>
            </div>
            <div v-for="(proposal, index) in proposals.slice(1)" :key="index" class="proposal-container">
                <div class="proposal-row">
                    {{ formatFullDate(proposal.submitted_at || proposal.created_at) }}

                    <div class="right-section">
                        <span v-if="shouldShowStatusBadge(proposal, index)" class="status-badge" :class="getStatusBadgeClass(proposal.status)">
                            {{ formatStatus(proposal.status) }}
                        </span>
                        <button class="accordion-toggle" @click="toggleAccordion(index)">
                            <span class="arrow" :class="{ 'rotated': openAccordions[index] }">▼</span>
                        </button>
                    </div>
                </div>
                <div v-show="openAccordions[index]" class="content-wrapper">
                    <div class="columns-wrapper">
                        <div class="proposal-image">
                            <img :src="proposal.activation.image" :alt="proposal.activation.public_name">
                        </div>
                        <div class="proposal-info">
                            <h3>{{ proposal.activation.public_name }}</h3>
                            <p class="submission-date">You submitted this proposal on {{ formatDate(proposal.submitted_at || proposal.created_at) }}</p>
                            <p class="notes-label">Your Notes:</p>
                        </div>
                    </div>

                    <div class="proposal-actions">
                        <p>You can now see the tasks required in order to complete this opportunity.</p>
                        <button class="view-details-btn" @click="navigateToOverview(proposal.id)">View Details</button>
                    </div>
                </div>
            </div>
        </template>
    </page-layout>
</template>

<script>
    import PageLayout from '../../layouts/PageLayout.vue';
    import PageTitle from '../../library/PageTitle.vue';
    import useRouter from '../../../../src/composables/use-router';
    import { ref, computed } from 'vue-demi';
    import moment from 'moment-timezone';
    import {
        ACTIVATION_BRIEF_STATUS_ACCEPTED,
        ACTIVATION_BRIEF_STATUS_IN_REVIEW,
        ACTIVATION_BRIEF_STATUS_LABELS,
        ACTIVATION_BRIEF_STATUS_COLORS
    } from "../../../constants/activation-brief";
    import ActivationBriefMixin from './mixins/ActivationBriefMixin';

    export default {
        name: 'ActivationProposalsView',
        components: {
            PageTitle,
            PageLayout,
        },
        mixins: [ActivationBriefMixin],
        props: {
            proposals: {
                type: Array,
                required: true
            },
        },
        setup(props) {
            const router = useRouter().get();
            const openAccordions = ref(new Array(props.proposals.length).fill(false));

            const featuredProposal = computed(() => props.proposals[0]);

            const shouldShowStatusBadge = (proposal, index) => {
                const isNotAccepted = proposal.status !== ACTIVATION_BRIEF_STATUS_ACCEPTED;
                const firstNotFeaturedAcceptedIndex = props.proposals.slice(1).findIndex(p => p.status === ACTIVATION_BRIEF_STATUS_ACCEPTED);
                const isFirstNotFeaturedAccepted = index === firstNotFeaturedAcceptedIndex;
                return isNotAccepted || (isFirstNotFeaturedAccepted && featuredProposal.value.status !== ACTIVATION_BRIEF_STATUS_ACCEPTED);
            };

            const toggleAccordion = (index) => {
                const newState = [...openAccordions.value];
                newState[index] = !newState[index];
                openAccordions.value = newState;
            };

            const handleClose = () => {
                router.push({ name: 'v2.activation.brief.overview' });
            };

            const navigateToOverview = (briefId) => {
                router.push({
                    name: 'v2.activation.brief.overview',
                    params: { brief_id: briefId }
                });
            };

            const getStatusBadgeClass = (status) => ({
                [ACTIVATION_BRIEF_STATUS_COLORS[status]]: true
            });

            return {
                handleClose,
                toggleAccordion,
                openAccordions,
                navigateToOverview,
                router,
                getStatusBadgeClass,
                ACTIVATION_BRIEF_STATUS_ACCEPTED,
                featuredProposal,
                shouldShowStatusBadge
            };
        }
    };
</script>

<style scoped lang="scss">
.proposal-container {
    display: flex;
    flex-direction: column;
    margin-bottom: 0;
    padding: 0.75rem 1rem;
    background: white;
    border-bottom: 1px solid #eee;

    &.featured-proposal {
        margin-bottom: 2rem;
    }

    &:last-child {
        border-bottom: none;
    }

    .columns-wrapper {
        display: flex;
        gap: 1rem;
        align-items: center;
    }

    .proposal-image {
        flex: 0 0 60px;
        img {
            width: 60px;
            height: 60px;
            object-fit: cover;
            border-radius: 4px;
        }
    }

    .proposal-info {
        flex: 1;
        h3 {
            font-size: 1rem;
            font-weight: 600;
            margin: 0 0 0.25rem 0;
            color: #333;
        }
        .submission-date {
            color: #666;
            font-size: 0.75rem;
            margin: 0 0 0.5rem 0;
        }
        .notes-label {
            color: #666;
            font-weight: 600;
            margin: 0;
        }
    }

    .proposal-status {
        flex: 0 0 auto;
    }

    .status-badge {
        display: inline-block;
        padding: 0.25rem 0.75rem;
        border-radius: 4px;
        font-size: 0.75rem;
        font-weight: 500;

        &.good {
            background: #e8f7f0;
            color: #00a86b;
            border: 1px solid #00a86b;
        }

        &.warning {
            background: #fff3e6;
            color: #ff9933;
            border: 1px solid #ff9933;
        }

        &.basic {
            background: white;
            color: #666;
            border: 1px solid #ccc;
        }

        &.primary {
            background: #e6f3ff;
            color: #0066cc;
            border: 1px solid #0066cc;
        }
    }

    .proposal-actions {
        background: #f8f9fa;
        margin: 0 -1rem -0.75rem -1rem;
        padding: 1rem;
        border-top: 1px solid #eee;

        p {
            margin: 0 0 1rem 0;
            color: #666;
        }

        .view-details-btn {
            padding: 0.5rem 1rem;
            background: white;
            border: 1px solid #ddd;
            border-radius: 4px;
            color: #666;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s;

            &:hover {
                background: #f5f5f5;
            }
        }
    }
}

.proposal-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: white;
    min-height: 32px;
    padding: 0.5rem 1rem;

    .right-section {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        margin-left: auto;

        .accordion-toggle {
            padding: 0.25rem;
            background: none;
            border: none;
            cursor: pointer;
            color: #666;

            .arrow {
                display: inline-block;
                transition: transform 0.2s ease;

                &.rotated {
                    transform: rotate(180deg);
                }
            }
        }
    }
}

.content-wrapper {
    padding: 1rem;
    background: white;
    border-bottom: 1px solid #eee;
}
</style>
